import { db } from "@/lib/firebase";
import { addDoc, collection, serverTimestamp, Timestamp } from "firebase/firestore";

/**
 * Append-only record of what each org member did, at
 * `organizations/{orgId}/user_actions_ledger/{entryId}`.
 *
 * Documents carry only the *current* author (`created_by`/`updated_by`); the
 * history — "Alice created it, then Bob edited it" — is this ledger, queried by
 * `target_path`.
 *
 * `firestore.rules` pins `actor_uid` to `request.auth.uid`, pins `at` to
 * `request.time`, and refuses updates and deletes. So an entry written here is
 * non-forgeable: it cannot be attributed to another member, back-dated, or
 * rewritten. It is *not* guaranteed complete — rules have no cross-document
 * constraint, so nothing forces this call to accompany the mutation it
 * describes. Losing an entry to a network failure loses history, not integrity.
 */

/**
 * Keep in sync with `ActionType` in core (`app/shared/models/action_ledger.py`).
 * Only the entity-mutation names below are written from here; the AI/workflow
 * names (`heuristic_audit.started`, `wordpress.*`, …) are emitted by core at
 * the endpoints that run them, so they never appear at a call site in this app.
 */
export type LedgerAction =
  | "domain.created"
  | "domain.deleted"
  | "page.created"
  | "page.deleted"
  | "policy.created"
  | "policy.updated"
  | "policy.deleted"
  | "integration.connected"
  | "integration.disconnected"
  | "member.added"
  | "member.removed"
  | "member.role_changed"
  | "member.ownership_transferred"
  | "organization.updated";

/** Matches core's `LEDGER_RETENTION`. The TTL policy on `expires_at` is currently disabled. */
const RETENTION_DAYS = 90;

export async function recordAction(params: {
  orgId: string;
  actorUid: string | undefined;
  action: LedgerAction;
  targetPath: string;
  domainId?: string | null;
  summary?: string | null;
}): Promise<void> {
  const { orgId, actorUid, action, targetPath, domainId, summary } = params;
  if (!orgId || !actorUid) return;

  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + RETENTION_DAYS);

  try {
    await addDoc(collection(db, `organizations/${orgId}/user_actions_ledger`), {
      at: serverTimestamp(),
      actor_uid: actorUid,
      // Always "user", and never `on_behalf_of`: firestore.rules rejects a
      // client create that claims either "agent"/"system" or that a machine
      // acted for someone. Those entries come from core and hermes.
      actor_type: "user",
      action,
      target_path: targetPath,
      domain_id: domainId ?? null,
      summary: summary ?? null,
      expires_at: Timestamp.fromDate(expiresAt),
    });
  } catch (error) {
    // Never fail the user's actual action because bookkeeping failed. The
    // mutation already succeeded (or, for deletes, is about to); surfacing this
    // as an error would misreport it as not having happened.
    console.error(`Failed to record ${action} on ${targetPath}:`, error);
  }
}
